<template>
    <header-banner title="Annuity"></header-banner>

    <div class="row mb-4">
        <div class="col-md-8 mx-auto">
            <div class="mb-5">
                <p>
                    <strong>What is an annuity?</strong>
                </p>
                <p>
                    An annuity is a retirement product offered by insurance companies that lets you exchange some or all of your pension for a regular income guaranteed for life.
                </p>
                <p>
                    You can typically choose to receive up to 25% as a tax-free cash lump sum, using the remainder to purchase the annuity. The income from the annuity is taxed
                    as earned income.
                </p>
            </div>

            <div>
                <p><strong>Understanding Annuity Features: What's included and why they matter</strong></p>

                <table class="table table-bordered align-middle text-start">
                    <thead class="text-center">
                        <tr>
                            <th class="pt-4 pb-2" scope="col">
                                <img src="/Content/images/HL/annuity_income.png"
                                    alt="Capital protection" 
                                    width="48" height="48"
                                    class="img-fluid">
                                <p class="mt-2">Annuity income options</p>
                            </th>
                            <th class="pt-4 pb-2" scope="col">
                                <img src="/Content/images/HL/annuity_payment.png"
                                    alt="Capital protection" 
                                    width="48" height="48"
                                    class="img-fluid">
                                <p class="mt-2">Annuity payment options</p>
                            </th>
                            <th class="pt-4 pb-2" scope="col">
                                <img src="/Content/images/HL/annuity_deathbenefits.png"
                                    alt="Capital protection" 
                                    width="48" height="48"
                                    class="img-fluid">
                                <p class="mt-2">Annuity death benefits</p>
                            </th>
                        </tr>
                    </thead>

                    <tbody class="align-top">
                        <tr>
                            <td class="px-4 pt-3 pb-2">An annuity payment can stay the same or increase each year. Increases will mean you start with less, but your income will be better protected from inflation. When choosing which option is right for you, you should keep in mind how your spending might change in retirement and how inflation increases prices over time.</td>
                            <td class="px-4 pt-3 pb-2">
                                <p>Choose to receive income monthly, quarterly, half-yearly or annually, either in advance (starting immediately) or in arrears (paid at the end of each period).</p>
                                <p>Choose how often you receive your income payments:</p>
                            </td>
                            <td class="px-4 pt-3 pb-2">You could choose an annuity beneficiary payment option which will pay your annuity income to your loved ones when you die, potentially tax free.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3">
                                <bullet-point-list :items="annuityIncomeBullets"></bullet-point-list>
                                <p class="pt-2">When choosing, consider how spending might change and how inflation could affect your future costs.</p>
                            </td>
                            <td class="px-4 py-3">
                                <bullet-point-list :items="annuityPaymentPeriodBullets"></bullet-point-list>
                                <hr />
                                <bullet-point-list :items="annuityPaymentTypeBullets"></bullet-point-list>
                            </td>
                            <td class="px-4 py-3">
                                <bullet-point-list :items="annuityDeathBullets"></bullet-point-list>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <div class="mt-5 mb-4">
                <h4>Enhanced annuities: boost your annuity income in minutes</h4>
            </div>
            <div class="mb-4">
                <p>
                    <strong>What is an enhanced annuity?</strong>
                </p>
                <p>
                    Enhanced annuities pay you a higher rate of income depending on your lifestyle choices and medical conditions.
                </p>
                <p>
                    Even confirming some basic details, could mean you get a higher income. For example, your height, weight, smoking history or relationship status.
                </p>
                <p>
                    It's important to confirm as much information as you can, including your partner's details to make sure you get the highest income possible.
                </p>
            </div>
            <div class="mb-4">
                <p>
                    <strong>What medical conditions qualify for an enhanced annuity?</strong>
                </p>
                <p>
                    Providers take into account minor to serious medical conditions when you get an enhanced quote. Common conditions include:
                </p>
                <ul>
                    <li>
                        High blood pressure
                    </li>
                    <li>
                        High cholesterol
                    </li>
                    <li>
                        Diabetes
                    </li>
                    <li>
                        Stroke
                    </li>
                </ul>
                <p>
                    There are lots of other conditions which could mean you get more for your money.
                </p>
                <p>
                    You can buy an annuity any time from age 55 (rising to 57 from 2028). Our annuity comparison tool helps you shop around the whole UK open market in minutes.
                </p>
            </div>
            <div class="mb-4">
                <p>
                    <strong>Do you need a medical examination for an enhanced annuity?</strong>
                </p>
                <p>
                    In most cases, you don't need to go for a medical examination. But some annuity providers might ask your GP for your medical reports depending on your
                    circumstances and health conditions. If you're a smoker, annuity providers might ask you to take a test to confirm this.
                </p>
                <p>
                    It's important you give accurate information when you request an enhanced annuity. If you don''t, the annuity rate you're offered might change or be withdrawn.
                </p>
            </div>
            <div class="mb-4">
                <p>
                    <strong>Next steps</strong>
                </p>
                <p>
                    Call one of our Retirement Specialists on 0117 314 1798 to discuss your options and have your questions answered. If you want to proceed with purchasing an annuity
                    they'll support you with this. You can also email the team at <a href="mailto:retire@hl.co.uk">retire@hl.co.uk</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import HeaderBanner from './HeaderBanner.vue';
import BulletPointList from '../common/BulletPointList.vue';
import { BulletListItem } from '@/guiide/ts/components/bulletListIconStyle';

const annuityIncomeBullets = new Array<BulletListItem>(
    { subtitle: "Level income:", text: "Same payment each year. Simple, but inflation may reduce its buying power over time." },
    { subtitle: "Increasing income:", text: "Payments will increase annually, usually by 3% or 5%. Starts lower but helps offset inflation." },
    { subtitle: "Inflation-linked income:", text: "Payments track the Retail Price Index (RPI), helping maintain your spending power as prices rise." },
)

const annuityPaymentPeriodBullets = new Array<BulletListItem>(
    { subtitle: "Monthly", text: "" },
    { subtitle: "Quarterly", text: "" },
    { subtitle: "Half-yearly", text: "" },
    { subtitle: "Annually", text: "" },
);

const annuityPaymentTypeBullets = new Array<BulletListItem>(
    { subtitle: "In advance:", text: "(starting immediately)." },
    { subtitle: "In arrears:", text: "(paid at the end of each period)." }
);

const annuityDeathBullets = new Array<BulletListItem>(
    { subtitle: "Single Life:", text: "Income stops when you die." },
    { subtitle: "Joint Life:", text: "A percentage (e.g. 50%, 66%, 100%) continues to your partner." },
    { subtitle: "Guarantee Period:", text: "Income is paid for a set time, even if you pass away." },
    { subtitle: "Value Protection:", text: "Returns unused funds to your beneficiaries." }
);

</script>

<style scoped>
    .mb-6 {
        margin-bottom: 2.25rem;
    }
</style>